import {
	type BaseState,
	type GameDefinition,
	type MoveOutcome,
	otherPlayer,
	type PlayerIndex,
	reject,
	resultFromScores,
} from "../types";

export const PITS_PER_SIDE = 6;
export const STONES_PER_PIT = 4;
/** 0–5 are player 0's pits, 6 is their store; 7–12 and 13 belong to player 1. */
export const STORE = [PITS_PER_SIDE, PITS_PER_SIDE * 2 + 1] as const;

export interface PitRowState extends BaseState {
	kind: "pitrow";
	board: number[];
	lastPit: number | null;
	lastNote: string | null;
}

export interface PitRowMove {
	pit: number;
}

const ownsPit = (player: PlayerIndex, pit: number): boolean =>
	player === 0 ? pit >= 0 && pit < PITS_PER_SIDE : pit > PITS_PER_SIDE && pit < STORE[1];

const oppositePit = (pit: number): number => PITS_PER_SIDE * 2 - pit;

function sweepUp(board: number[]): number[] {
	const swept = [...board];
	// Whoever still has stones keeps them when the other side empties.
	for (let pit = 0; pit < PITS_PER_SIDE; pit++) {
		swept[STORE[0]] += swept[pit];
		swept[pit] = 0;
	}
	for (let pit = PITS_PER_SIDE + 1; pit < STORE[1]; pit++) {
		swept[STORE[1]] += swept[pit];
		swept[pit] = 0;
	}
	return swept;
}

function sideEmpty(board: number[], player: PlayerIndex): boolean {
	const from = player === 0 ? 0 : PITS_PER_SIDE + 1;
	const to = player === 0 ? PITS_PER_SIDE : STORE[1];
	for (let pit = from; pit < to; pit++) if (board[pit] > 0) return false;
	return true;
}

export const pitRow: GameDefinition<PitRowState, PitRowMove> = {
	id: "pitrow",
	category: "classics",
	title: "Pit Row",
	emoji: "🫘",
	blurb: "Scoop up stones and drop them one by one. Fill your store to win.",
	howTo: [
		"Pick one of your pits and scoop up all its stones.",
		"Drop one stone in each pit going round, including your own store.",
		"Land your last stone in your store and you go again.",
		"Land in an empty pit on your side and you capture the stones opposite.",
		"Most stones in your store at the end wins.",
	],

	create(rng) {
		const board = Array<number>(PITS_PER_SIDE * 2 + 2).fill(STONES_PER_PIT);
		board[STORE[0]] = 0;
		board[STORE[1]] = 0;
		return {
			kind: "pitrow",
			board,
			lastPit: null,
			lastNote: null,
			// The opener has the tempo, so it is drawn by lot rather than handed
			// to whoever happens to sit in seat 0.
			turn: rng.int(2) as PlayerIndex,
			scores: [0, 0],
			result: null,
		};
	},

	parseMove(raw) {
		if (typeof raw !== "object" || raw === null) return null;
		const pit = (raw as { pit?: unknown }).pit;
		if (typeof pit !== "number" || !Number.isInteger(pit)) return null;
		if (pit < 0 || pit >= PITS_PER_SIDE * 2 + 2) return null;
		return { pit };
	},

	apply(state, player, move): MoveOutcome<PitRowState> {
		if (state.result) return reject("This game is already finished.");
		if (state.turn !== player) return reject("It's not your turn yet.");
		if (!ownsPit(player, move.pit)) return reject("That's not one of your pits.");
		if (state.board[move.pit] === 0) return reject("That pit is empty.");

		const board = [...state.board];
		let stones = board[move.pit];
		board[move.pit] = 0;
		let pit = move.pit;
		const theirStore = STORE[otherPlayer(player)];

		while (stones > 0) {
			pit = (pit + 1) % board.length;
			// You never feed your opponent's store.
			if (pit === theirStore) continue;
			board[pit] += 1;
			stones--;
		}

		let note: string | null = null;
		let again = false;

		if (pit === STORE[player]) {
			again = true;
			note = "Landed in your store — go again!";
		} else if (ownsPit(player, pit) && board[pit] === 1 && board[oppositePit(pit)] > 0) {
			// The classic capture: an empty pit of yours takes what faces it.
			const captured = board[oppositePit(pit)] + 1;
			board[STORE[player]] += captured;
			board[pit] = 0;
			board[oppositePit(pit)] = 0;
			note = `Captured ${captured} stones!`;
		}

		const finished = sideEmpty(board, 0) || sideEmpty(board, 1);
		const final = finished ? sweepUp(board) : board;
		const scores: [number, number] = [final[STORE[0]], final[STORE[1]]];

		return {
			ok: true,
			state: {
				...state,
				board: final,
				scores,
				lastPit: move.pit,
				lastNote: finished ? "The last stones go to whoever still had some." : note,
				turn: finished ? null : again ? player : otherPlayer(player),
				result: finished ? resultFromScores(scores) : null,
			},
		};
	},

	legalMoves(state, player) {
		if (state.result || state.turn !== player) return [];
		const moves: PitRowMove[] = [];
		const from = player === 0 ? 0 : PITS_PER_SIDE + 1;
		const to = player === 0 ? PITS_PER_SIDE : STORE[1];
		for (let pit = from; pit < to; pit++) {
			if (state.board[pit] > 0) moves.push({ pit });
		}
		return moves;
	},

	tick() {
		return null;
	},

	nextTickAt() {
		return null;
	},
};
